<template>
  <div class="pa-4">
    <div class="d-flex justify-space-between align-center mb-4">
      <h2>Eigenschappen</h2>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Eigenschap Toevoegen
      </v-btn>
    </div>

    <!-- Loading State -->
    <v-progress-linear
      v-if="store.loading || categoriesStore.loading"
      indeterminate
    ></v-progress-linear>

    <!-- Error State -->
    <v-alert v-if="store.error" type="error" class="mb-4">
      {{ store.error }}
    </v-alert>

    <!-- Empty State -->
    <v-card
      v-if="!store.loading && store.traits.length === 0"
      class="pa-8 text-center"
    >
      <v-icon size="64" color="grey-lighten-1" class="mb-4"
        >mdi-tag-outline</v-icon
      >
      <h3 class="text-h6 mb-2">Geen Eigenschappen</h3>
      <p class="text-body-2 text-medium-emphasis mb-4">
        Begin door je eerste eigenschap te maken.
      </p>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreateDialog">
        Eigenschap Toevoegen
      </v-btn>
    </v-card>

    <!-- Traits List -->
    <v-card v-if="!store.loading && store.traits.length > 0">
      <v-list>
        <v-list-item
          v-for="trait in store.traits"
          :key="trait.id"
          class="border-b"
        >
          <template #prepend>
            <v-icon :color="trait.color || 'primary'">{{
              trait.icon || "mdi-tag"
            }}</v-icon>
          </template>

          <div class="d-flex flex-column">
            <v-list-item-title>
              {{ trait.name }}
              <v-chip
                v-if="!trait.active"
                size="small"
                color="warning"
                variant="outlined"
                class="ml-2"
              >
                Inactief
              </v-chip>
            </v-list-item-title>
            <v-list-item-subtitle v-if="trait.category">{{
              trait.category
            }}</v-list-item-subtitle>
          </div>

          <template #append>
            <div class="d-flex ga-2">
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                @click="openEditDialog(trait)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                size="small"
                variant="text"
                color="error"
                @click="confirmDelete(trait)"
              ></v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? "Eigenschap Bewerken" : "Eigenschap Toevoegen" }}
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="formData.name"
              label="Naam"
              :rules="nameRules"
              required
              autofocus
            ></v-text-field>

            <v-select
              v-model="formData.trait_category_id"
              label="Categorie"
              :items="categoryOptions"
              item-title="label"
              item-value="value"
              :rules="categoryRules"
              :loading="categoriesStore.loading"
              required
            ></v-select>

            <v-switch
              v-model="formData.active"
              label="Actief"
              color="primary"
              hide-details
              class="mb-4"
            ></v-switch>

            <v-textarea
              v-model="formData.ai_guidance"
              label="Wanneer van toepassing (voor de AI-assistent)"
              hint="Bijv.: de persoon heeft geen vaste slaapplek. Helpt de assistent dit kenmerk goed voor te stellen."
              persistent-hint
              variant="outlined"
              auto-grow
              rows="2"
              counter="1000"
              :rules="aiGuidanceRules"
              class="mb-4"
            ></v-textarea>

            <h4 class="text-subtitle-1 mb-3">Weergave</h4>

            <ColorPickerField
              v-model="formData.color"
              label="Kleur"
              hint="Laat leeg voor standaard kleur"
            />

            <IconPickerField
              v-model="formData.icon"
              label="Icoon"
              :preview-color="formData.color || 'primary'"
              placeholder="Bijv: mdi-star, mdi-heart"
            />

            <!-- Preview -->
            <div
              v-if="formData.name && (formData.icon || formData.color)"
              class="mb-4"
            >
              <div class="text-caption mb-2">Voorbeeld:</div>
              <v-chip variant="tonal" label>
                <template #prepend>
                  <v-icon
                    :color="formData.color"
                    size="small"
                    class="trait-icon"
                  >
                    {{ formData.icon || "mdi-tag" }}
                  </v-icon>
                </template>
                {{ formData.name }}
              </v-chip>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Annuleren</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!formValid"
            @click="save"
          >
            {{ isEditing ? "Bijwerken" : "Maken" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Verwijdering Bevestigen</v-card-title>
        <v-card-text>
          Weet je zeker dat je "{{ traitToDelete?.name }}" wilt verwijderen?
          Deze actie kan niet ongedaan worden gemaakt.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">Annuleren</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteTrait">
            Verwijderen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from "vue";
import { useTraitsStoreRefactor } from "@/stores/traitsStoreRefactor";
import { useTraitCategoriesStoreRefactor } from "@/stores/traitCategoriesStoreRefactor";
import ColorPickerField from "@/components/ui/ColorPickerField.vue";
import IconPickerField from "@/components/ui/IconPickerField.vue";

const store = useTraitsStoreRefactor();
const categoriesStore = useTraitCategoriesStoreRefactor();

// Data
const dialog = ref(false);
const deleteDialog = ref(false);
const formValid = ref(false);
const saving = ref(false);
const deleting = ref(false);
const isEditing = ref(false);
const traitToDelete = ref(null);

const formData = reactive({
  id: null,
  name: "",
  trait_category_id: null,
  active: true,
  icon: "",
  color: "",
  ai_guidance: "",
});

// Computed
const categoryOptions = computed(() => {
  return categoriesStore.selectItems.map((category) => ({
    label: category.name, // Use name instead of label
    value: category.value,
  }));
});

// Form validation rules
const nameRules = [
  (v) => !!v || "Naam is verplicht",
  (v) => (v && v.length <= 255) || "Naam moet minder dan 255 karakters zijn",
];

const categoryRules = [(v) => !!v || "Categorie is verplicht"];

const aiGuidanceRules = [
  (v) => !v || v.length <= 1000 || "Maximaal 1000 karakters",
];

// Methods
const openCreateDialog = () => {
  isEditing.value = false;
  formData.id = null;
  formData.name = "";
  formData.trait_category_id = null;
  formData.active = true;
  formData.icon = "";
  formData.color = "";
  formData.ai_guidance = "";
  dialog.value = true;
};

const openEditDialog = (trait) => {
  isEditing.value = true;
  formData.id = trait.id;
  formData.name = trait.name;
  // Find the trait_category_id from the trait data
  const category = categoriesStore.categories.find(
    (cat) => cat.name === trait.category,
  );
  formData.trait_category_id = category ? category.id : null;
  formData.active = trait.active ?? true;
  formData.icon = trait.icon || "";
  formData.color = trait.color || "";
  formData.ai_guidance = trait.ai_guidance || "";
  dialog.value = true;
};

const closeDialog = () => {
  dialog.value = false;
  if (formData.form) {
    formData.form.reset();
  }
};

const save = async () => {
  if (!formValid.value) return;

  saving.value = true;

  try {
    const traitData = {
      name: formData.name,
      trait_category_id: formData.trait_category_id,
      active: formData.active,
      icon: formData.icon || null,
      color: formData.color || null,
      ai_guidance: formData.ai_guidance || null,
    };

    if (isEditing.value) {
      traitData.id = formData.id;
      await store.update(traitData);
    } else {
      await store.create(traitData);
    }

    closeDialog();
  } catch (error) {
    console.error("Error saving trait:", error);
  } finally {
    saving.value = false;
  }
};

const confirmDelete = (trait) => {
  traitToDelete.value = trait;
  deleteDialog.value = true;
};

const deleteTrait = async () => {
  if (!traitToDelete.value) return;

  deleting.value = true;

  try {
    await store.destroy(traitToDelete.value.id);
    deleteDialog.value = false;
    traitToDelete.value = null;
  } catch (error) {
    console.error("Error deleting trait:", error);
  } finally {
    deleting.value = false;
  }
};

// Load data on mount
onMounted(async () => {
  await Promise.all([store.fetchAll(), categoriesStore.fetchAll()]);
});
</script>

<style scoped>
.trait-icon {
  margin-right: 4px !important;
}
</style>
